import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import type {
  InspectionPreviewResult,
  InspectionResult,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import { inspectionProvenance } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import isNil from 'lodash/isNil'
import { Box as BoxIcon, FileCheck2, PanelRightOpen } from 'lucide-react'
import styled from 'styled-components'

import { InlineTurnCard } from './docChatStyles'
import { InspectionFindingActions } from './InspectionFindingActions'
import type { PreviewInspectionHandler } from './previewDocumentHandler'

const Metrics = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 1rem;
  margin: 0;
  dd {
    margin: 0.25rem 0 0;
  }
`
const Details = styled.details`
  border-top: 1px solid var(--border);
  padding-top: 0.75rem;
  summary {
    cursor: pointer;
  }
  summary:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 0.25rem;
  }
`
const Sources = styled.dl`
  display: grid;
  grid-template-columns: minmax(7rem, 1fr) minmax(0, 2fr);
  gap: 0.5rem;
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
`

export function InspectionCalculationDetails({
  result,
  onPreview,
}: {
  result: InspectionResult
  onPreview?: PreviewInspectionHandler
}) {
  const { i18n } = useLingui()
  const assetUrl =
    result.unit_id && result.site_id && result.organization_id
      ? `/assets/routing?${new URLSearchParams({ unitId: result.unit_id, siteId: result.site_id, orgId: result.organization_id })}`
      : null
  const data = result.calculation?.result
  const source = inspectionProvenance(result)
  const count = (value: number | null | undefined) =>
    isNil(value) ? '—' : i18n.number(value)
  const capped =
    !isNil(data?.bins_total) &&
    data?.bins_total > 0 &&
    data?.bins_at_upper_limit === data?.bins_total
  const unavailable =
    Boolean(data?.error) || isNil(data?.min_remaining_life_years)
  const years = isNil(data?.min_remaining_life_years)
    ? '—'
    : i18n.number(data?.min_remaining_life_years, { maximumFractionDigits: 1 })
  const capCount = count(data?.bins_at_upper_limit)
  const bounds = source.bin_bounds
  const {
    x_start: xStart,
    x_end: xEnd,
    y_start: yStart,
    y_end: yEnd,
  } = bounds ?? {}
  const floorReached =
    !isNil(source.minimum_floor_years) &&
    !isNil(data?.min_remaining_life_years) &&
    data?.min_remaining_life_years <= source.minimum_floor_years

  return (
    <InlineTurnCard>
      <Text as='h3' variant='label' weight='medium'>
        {result.kind === 'remaining_life' ? (
          <Trans>Inspection · Remaining life</Trans>
        ) : (
          <Trans>Inspection view</Trans>
        )}
      </Text>
      <Text as='p' variant='body'>
        {result.unit_label ? `${result.unit_label} · ` : ''}
        {result.component_label ?? <Trans>Inspection</Trans>}
      </Text>
      {result.kind === 'inspection_view' ? (
        <Text as='p' variant='caption'>
          {source.inspection_date?.slice(0, 10)}
        </Text>
      ) : unavailable ? (
        <Text as='p' variant='body'>
          <Trans>Remaining life unavailable for this selection.</Trans>
        </Text>
      ) : (
        <>
          <Text as='p' variant='body' weight='medium'>
            {capped ? (
              <Trans>At calculation limit</Trans>
            ) : floorReached ? (
              <Trans>At calculation floor</Trans>
            ) : (
              <Trans>{years} years · calculated minimum</Trans>
            )}
          </Text>
          <Metrics>
            <div>
              <dt>
                <Text as='span' variant='caption'>
                  <Trans>Finite result bins</Trans>
                </Text>
              </dt>
              <dd>
                <Text as='span' variant='body'>
                  {count(data?.bins_total)}
                </Text>
              </dd>
            </div>
            <div>
              <dt>
                <Text as='span' variant='caption'>
                  <Trans>Below minimum thickness</Trans>
                </Text>
              </dt>
              <dd>
                <Text as='span' variant='body'>
                  {count(data?.bins_below_tmin)}
                </Text>
              </dd>
            </div>
            <div>
              <dt>
                <Text as='span' variant='caption'>
                  <Trans>Excluded bins</Trans>
                </Text>
              </dt>
              <dd>
                <Text as='span' variant='body'>
                  {count(data?.bins_excluded)}
                </Text>
              </dd>
            </div>
          </Metrics>
        </>
      )}
      {!isNil(data?.bins_at_upper_limit) && data?.bins_at_upper_limit > 0 ? (
        <Text as='p' variant='caption'>
          <Trans>
            {capCount} bins reached the calculation cap. These are not precise
            life estimates.
          </Trans>
        </Text>
      ) : null}
      {result.kind === 'remaining_life' ? (
        <Text as='p' variant='caption' color='muted'>
          <Trans>
            Calculated from available bins; not an inspection interval or an
            asset pass/fail assessment.
          </Trans>
        </Text>
      ) : null}
      {assetUrl ? (
        <div>
          <Button asChild variant='outline' size='sm'>
            <a href={assetUrl}>
              <Trans>Open asset</Trans>
            </a>
          </Button>
        </div>
      ) : null}
      {onPreview ? (
        <div>
          <Button variant='outline' size='sm' onClick={() => onPreview(result)}>
            <Trans>View inspection</Trans>
          </Button>
        </div>
      ) : null}
      <Details>
        <summary>
          <Trans>Scope and calculation details</Trans>
        </summary>
        <Sources>
          <dt>
            <Text as='span' variant='caption'>
              <Trans>Inspection</Trans>
            </Text>
          </dt>
          <dd>
            <Text as='span' variant='caption'>
              {source.inspection_id ?? '—'}
            </Text>
          </dd>
          <dt>
            <Text as='span' variant='caption'>
              <Trans>Inspection date</Trans>
            </Text>
          </dt>
          <dd>
            <Text as='span' variant='caption'>
              {source.inspection_date?.slice(0, 10) ?? '—'}
            </Text>
          </dd>
          <dt>
            <Text as='span' variant='caption'>
              <Trans>Calculated as of</Trans>
            </Text>
          </dt>
          <dd>
            <Text as='span' variant='caption'>
              {source.as_of_date ?? '—'}
            </Text>
          </dd>
          <dt>
            <Text as='span' variant='caption'>
              <Trans>Selection</Trans>
            </Text>
          </dt>
          <dd>
            <Text as='span' variant='caption'>
              {bounds ? (
                <Trans>
                  Bin columns [{xStart}, {xEnd}), rows [{yStart}, {yEnd})
                </Trans>
              ) : (
                <Trans>Whole grid</Trans>
              )}
            </Text>
          </dd>
          <dt>
            <Text as='span' variant='caption'>
              <Trans>Dataset</Trans>
            </Text>
          </dt>
          <dd>
            <Text as='span' variant='caption'>
              {source.dataset_id ?? '—'}
            </Text>
          </dd>
          <dt>
            <Text as='span' variant='caption'>
              <Trans>Asset model</Trans>
            </Text>
          </dt>
          <dd>
            <Text as='span' variant='caption'>
              {source.asset_model_id ?? '—'}
            </Text>
          </dd>
        </Sources>
        <Text as='p' variant='caption'>
          {source.forecast_config_id ? (
            <Trans>
              Uses current component configuration with the inspection-linked
              asset model.
            </Trans>
          ) : (
            <Trans>
              No component configuration was provided; default forecast settings
              may apply.
            </Trans>
          )}
        </Text>
        <Text as='p' variant='caption' color='muted'>
          <Trans>
            Excluded cells have no finite calculated result. This is not an
            inspection coverage percentage.
          </Trans>
        </Text>
        {data?.error ? (
          <Text as='p' variant='caption'>
            {data?.error}
          </Text>
        ) : null}
      </Details>
    </InlineTurnCard>
  )
}

const PreviewCard = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 1rem;
  text-align: left;
  color: var(--foreground);
  background: var(--card);
  border: 0.0625rem solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
  > svg {
    width: 1.125rem;
    height: 1.125rem;
    flex-shrink: 0;
    color: var(--muted-foreground);
  }
  &:hover {
    background: var(--muted);
  }
  &:focus-visible {
    outline: 0.125rem solid var(--ring);
    outline-offset: 0.125rem;
  }
`
const CardCopy = styled.div`
  flex: 1;
  min-width: 0;
`
export function DocChatInspectionCard({
  result,
  onPreview,
}: {
  result: InspectionPreviewResult
  onPreview?: PreviewInspectionHandler
}) {
  if (result.kind === 'report')
    return (
      <PreviewCard
        type='button'
        onClick={() => onPreview?.(result)}
        disabled={!onPreview}>
        <FileCheck2 />
        <CardCopy>
          <Text as='p' weight='medium'>
            {result.presentation.title || result.title}
          </Text>
        </CardCopy>
        <PanelRightOpen />
      </PreviewCard>
    )
  const isNii = result.kind === 'nii_assessment'
  const title =
    result.presentation?.title ||
    (isNii
      ? result.title
      : [result.unit_label, result.component_label].filter(Boolean).join(' · '))
  return (
    <div>
      <PreviewCard
        type='button'
        onClick={() => onPreview?.(result)}
        disabled={!onPreview}>
        {isNii ? <FileCheck2 /> : <BoxIcon />}
        <CardCopy>
          <Text as='p' variant='body' weight='medium'>
            {title || <Trans>Inspection</Trans>}
          </Text>
          <Text as='p' variant='caption' color='muted'>
            {isNii ? (
              <Trans>NII assessment</Trans>
            ) : (
              <Trans>Interactive inspection</Trans>
            )}
            {' · '}
            {isNii
              ? result.analysis_date
              : inspectionProvenance(result).inspection_date?.slice(0, 10)}
          </Text>
        </CardCopy>
        <PanelRightOpen />
      </PreviewCard>
      {!isNii && result.findings?.length && onPreview ? (
        <InspectionFindingActions
          findings={result.findings}
          onSelect={(finding) =>
            onPreview({ ...result, selected_finding_id: finding.id })
          }
        />
      ) : null}
    </div>
  )
}
